import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Progress,
  Skeleton,
  Empty,
  Divider,
  Button,
} from "antd";
import {
  RocketOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import confetti from "canvas-confetti";
import { useAuth } from "../contexts/AuthContext";
import { DashboardService, DashboardStats } from "../services/dashboardService";
import {
  ProductFeaturesService,
  ProductFeature,
} from "../services/productFeaturesService";
import { BrandService, BrandMaturity } from "../services/brandService";
import { getActiveBrandId } from "../config/api";
import ProductCard from "../components/products/ProductCard";
import BrandMaturityDrawer from "../components/BrandMaturityDrawer";

const { Title, Text } = Typography;

const MATURITY_CELEBRATED_KEY = "fc_maturity_celebrated";

const getCelebratedKey = (brandId: string) => `${MATURITY_CELEBRATED_KEY}_${brandId}`;

const hasMaturityBeenCelebrated = (brandId: string | undefined): boolean => {
  if (!brandId || typeof window === "undefined") return false;
  return localStorage.getItem(getCelebratedKey(brandId)) === "true";
};

const setMaturityCelebrated = (brandId: string) => {
  try {
    localStorage.setItem(getCelebratedKey(brandId), "true");
  } catch {
    // localStorage may be full or disabled
  }
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<ProductFeature[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [maturity, setMaturity] = useState<BrandMaturity | null>(null);
  const [maturityDrawerOpen, setMaturityDrawerOpen] = useState(false);
  const [celebrationShown, setCelebrationShown] = useState(false);

  const activeBrandId = getActiveBrandId(user);
  const isFirstTime100 =
    maturity?.percentage === 100 &&
    activeBrandId &&
    !hasMaturityBeenCelebrated(activeBrandId);
  const showCelebrationCard =
    maturity?.percentage === 100 &&
    activeBrandId &&
    (isFirstTime100 || celebrationShown);

  useEffect(() => {
    if (isFirstTime100 && activeBrandId) {
      setMaturityCelebrated(activeBrandId);
      setCelebrationShown(true);

      const duration = 2 * 1000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ["#667eea", "#764ba2", "#52c41a"],
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ["#667eea", "#764ba2", "#52c41a"],
        });
        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();
    }
  }, [isFirstTime100, activeBrandId]);

  useEffect(() => {
    loadDashboardData();
    loadProductFeatures();
    loadBrandMaturity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrandId]);

  const loadDashboardData = async () => {
    if (!activeBrandId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const dashboardStats =
        await DashboardService.getDashboardStats(activeBrandId);
      setStats(dashboardStats);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadProductFeatures = async () => {
    if (!activeBrandId) {
      setProductsLoading(false);
      return;
    }

    try {
      setProductsLoading(true);
      const features =
        await ProductFeaturesService.getProductFeatures(activeBrandId);
      setProducts(features);
    } catch (error) {
      console.error("Failed to load product features:", error);
    } finally {
      setProductsLoading(false);
    }
  };

  const loadBrandMaturity = async () => {
    if (!activeBrandId) return;

    try {
      const data = await BrandService.getBrandMaturity(activeBrandId);
      setMaturity(data);
    } catch (error) {
      console.error("Failed to load brand maturity:", error);
    }
  };

  const handleEnableProduct = async (code: string) => {
    if (!activeBrandId) return;
    await ProductFeaturesService.enableProduct(activeBrandId, code);
    await loadProductFeatures(); // Reload to get updated data
  };

  const handleDisableProduct = async (code: string) => {
    if (!activeBrandId) return;
    await ProductFeaturesService.disableProduct(activeBrandId, code);
    await loadProductFeatures(); // Reload to get updated data
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 1 }} style={{ marginBottom: 24 }} />
        <Row gutter={[16, 16]}>
          {[1, 2, 3, 4].map((i) => (
            <Col xs={24} sm={12} lg={6} key={i}>
              <Card>
                <Skeleton active />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Dashboard
        </Title>
        <Text type="secondary">
          Hoş geldiniz, {user?.name}! İşte markanızın güncel durumu.
        </Text>
      </div>

      <Card
        style={{
          marginBottom: 24,
          borderRadius: 16,
          border: "1px solid #e6f4ff",
          background: "linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)",
        }}
        bodyStyle={{ padding: 20 }}
      >
        <Row align="middle" gutter={[16, 16]}>
          <Col flex={1}>
            <Title level={4} style={{ margin: 0 }}>
              FashMotion
            </Title>
            <Text type="secondary">
              Boydan kıyafet görselinden, ürün kimliği korunmuş kısa video üret.
            </Text>
          </Col>
          <Col>
            <Button type="primary" onClick={() => navigate('/fashion-motion')}>
              FashMotion'u aç
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Brand Maturity Card */}
      {maturity && maturity.percentage < 100 && (
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 16,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
          }}
          bodyStyle={{ padding: 20 }}
        >
          <Row align="middle" gutter={16}>
            <Col>
              <Progress
                type="circle"
                percent={maturity.percentage}
                size={80}
                strokeColor="#fff"
                trailColor="rgba(255,255,255,0.3)"
                format={(percent) => (
                  <span style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}>
                    {percent}%
                  </span>
                )}
              />
            </Col>
            <Col flex={1}>
              <div style={{ color: "white" }}>
                <Title level={4} style={{ color: "white", margin: "0 0 4px 0" }}>
                  <RocketOutlined style={{ marginRight: 8 }} />
                  Marka Kurulumu
                </Title>
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  {maturity.completedSteps} / {maturity.totalSteps} adım tamamlandı
                </Text>
                <br />
                <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                  Tüm özellikleri kullanmak için kurulumu tamamlayın
                </Text>
              </div>
            </Col>
            <Col>
              <Button
                type="default"
                size="large"
                onClick={() => setMaturityDrawerOpen(true)}
                style={{
                  borderColor: "white",
                  color: "#667eea",
                  background: "white",
                  fontWeight: 500,
                }}
              >
                Kurulumu Tamamla
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* Brand Maturity Success Card - Show only once (localStorage) */}
      {showCelebrationCard && (
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 16,
            border: "none",
            background: "linear-gradient(135deg, #52c41a 0%, #73d13d 100%)",
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Row align="middle" gutter={16}>
            <Col>
              <CheckCircleOutlined
                style={{ color: "white", fontSize: 56 }}
              />
            </Col>
            <Col flex={1}>
              <Title level={4} style={{ color: "white", margin: "0 0 4px 0" }}>
                Tebrikler! Gerekli tüm bilgileri doldurdunuz.
              </Title>
              <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: 14 }}>
                Marka kurulumunuz tamamlandı. Fashion Connect'in tüm özelliklerini kullanmaya hazırsınız.
              </Text>
            </Col>
          </Row>
        </Card>
      )}

      {/* Products Section */}
      <Divider style={{ margin: "32px 0" }} />

      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 8 }}>
          Ürünler
        </Title>
        <Text type="secondary">
          Fashion Connect ürünlerini yönetin ve kullanım metriklerini
          görüntüleyin
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        {productsLoading ? (
          // Loading skeleton
          [1, 2, 3].map((key) => (
            <Col xs={24} md={12} lg={8} key={key}>
              <Card>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Card>
            </Col>
          ))
        ) : products && products.length > 0 ? (
          // Product cards
          products.map((product) => (
            <Col xs={24} md={12} lg={8} key={product.code}>
              <ProductCard
                product={product}
                onEnable={handleEnableProduct}
                onDisable={handleDisableProduct}
                userRole={user?.role || "employee"}
                loading={productsLoading}
              />
            </Col>
          ))
        ) : (
          // Empty state
          <Col span={24}>
            <Card>
              <Empty
                description="Ürün bilgisi yüklenemedi"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          </Col>
        )}
      </Row>

      {/* Brand Maturity Drawer */}
      <BrandMaturityDrawer
        open={maturityDrawerOpen}
        onClose={() => setMaturityDrawerOpen(false)}
        onUpdate={loadBrandMaturity}
      />
    </div>
  );
};

export default Dashboard;
